import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import * as z from "zod";
import { readAskRedoConfirmationData } from "../../confirmation-data";

/**
 * Steps stay `unknown` here: the return-flow codec owns their shape and
 * validates them when the preview canvas is built.
 */
const flowChangeSchema = z.object({
  step: z.string(),
  label: z.string(),
  before: z.string().optional(),
  after: z.string().optional(),
});

const flowEditPreviewSchema = z.object({
  kind: z.literal("return-flow-edit-preview"),
  flowType: z.enum(FlowType),
  summary: z.string(),
  // Absent on approvals staged before the structured diff existed; the card
  // then falls back to the summary text.
  changes: z.array(flowChangeSchema).optional(),
  steps: z.array(z.unknown()),
  nodePositions: z.array(z.object({ x: z.number(), y: z.number() })).optional(),
  highlightedNodeIds: z.array(z.string()),
  highlightedEdgePairs: z.array(z.string()),
});

export type FlowChangeDescription = z.infer<typeof flowChangeSchema>;

/** A proposed return-flow edit staged for merchant approval. */
export type FlowEditPreview = z.infer<typeof flowEditPreviewSchema> & {
  confirmationPrompt: string | undefined;
};

/**
 * Reads a flow-edit preview out of a tool part's confirmation data. Returns
 * undefined for every other approval, so callers fall back to generic copy.
 */
export function readFlowEditPreview(
  input: unknown,
): FlowEditPreview | undefined {
  const confirmation = readAskRedoConfirmationData(input);
  if (!confirmation) {
    return undefined;
  }
  const parsed = flowEditPreviewSchema.safeParse(confirmation.confirmationData);
  if (!parsed.success) {
    return undefined;
  }
  return {
    ...parsed.data,
    confirmationPrompt: confirmation.confirmationPrompt,
  };
}

const returnFlowStagedSchema = z.object({
  kind: z.literal("return-flow-staged"),
  flowType: z.enum(FlowType),
});

/** A staged (saved-not-published) flow edit, read from a tool result. */
export function readReturnFlowStaged(
  output: unknown,
): { flowType: FlowType } | undefined {
  const parsed = returnFlowStagedSchema.safeParse(output);
  return parsed.success ? { flowType: parsed.data.flowType } : undefined;
}

/**
 * Settings routes for the flow families a return-flow edit can target. Flow
 * types with no editor route are omitted; callers then hide the link.
 */
const FLOW_TYPE_SETTINGS_SLUG: Partial<Record<FlowType, string>> = {
  [FlowType.RETURN]: "returns",
  [FlowType.FINALIZE_RETURN]: "returns-finalize",
  [FlowType.CLAIM]: "claims",
  [FlowType.FINALIZE_CLAIM]: "claims-finalize",
  [FlowType.WARRANTY]: "warranties",
  [FlowType.FINALIZE_WARRANTY]: "warranties-finalize",
  [FlowType.WARRANTY_REGISTRATION]: "warranties-registration",
  [FlowType.WITHDRAWAL]: "withdrawals",
};

/** Query param used to open a specific published flow version read-only. */
export const FLOW_EDITOR_VERSION_PARAM = "version";

/** The live flow editor for `flowType`, or undefined when it has no route. */
export function flowEditorHref(
  teamId: string,
  flowType: FlowType,
  versionId?: string,
): string | undefined {
  const slug = FLOW_TYPE_SETTINGS_SLUG[flowType];
  if (!slug) {
    return undefined;
  }
  const href = `/stores/${teamId}/settings/customer-portal/flows/${slug}`;
  return versionId
    ? `${href}?${FLOW_EDITOR_VERSION_PARAM}=${encodeURIComponent(versionId)}`
    : href;
}
